import { DROP_TABLES, RARITY_STAT_MULT, SLOT_BASE_STATS, SLOT_NOUN, TIER_NAME, TIER_STAT_MULT } from '../data/loot.ts';
import {
  GEAR_SLOTS,
  LEGENDARY_PITY,
  type GearSlot,
  type Item,
  type ItemStats,
  type PityState,
  type Rarity,
  type Tier,
} from '../model/types.ts';
import { normalizeSeed, rngNext } from './rng.ts';

export function publishedOdds(tier: Tier): { rarity: Rarity; percent: number }[] {
  const table = DROP_TABLES[tier];
  const total = table.reduce((sum, row) => sum + row.weight, 0);
  return table.map((row) => ({
    rarity: row.rarity,
    percent: (row.weight / total) * 100,
  }));
}

export function rollRarity(unit: number, tier: Tier, pity: PityState): { rarity: Rarity; pity: PityState } {
  const table = DROP_TABLES[tier];
  const total = table.reduce((sum, row) => sum + row.weight, 0);
  let cursor = Math.max(0, Math.min(0.999999, unit)) * total;
  let rarity: Rarity = table[0]?.rarity ?? 'common';
  for (const row of table) {
    cursor -= row.weight;
    if (cursor < 0) {
      rarity = row.rarity;
      break;
    }
  }
  return applyPity(rarity, pity);
}

/**
 * After LEGENDARY_PITY Epic-or-better drops that were not Legendary,
 * the next Epic-or-better drop is forced to Legendary.
 * A natural Legendary resets the counter. Rarities below Epic do not move it.
 */
export function applyPity(rarity: Rarity, pity: PityState): { rarity: Rarity; pity: PityState } {
  const epicPlus = rarity === 'epic' || rarity === 'legendary';
  if (!epicPlus) return { rarity, pity: { ...pity } };
  if (pity.epicPlusSinceLegendary >= LEGENDARY_PITY || rarity === 'legendary') {
    return { rarity: 'legendary', pity: { epicPlusSinceLegendary: 0 } };
  }
  return {
    rarity,
    pity: { epicPlusSinceLegendary: pity.epicPlusSinceLegendary + 1 },
  };
}

export function makeItem(args: {
  id: string;
  slot: GearSlot;
  tier: Tier;
  rarity: Rarity;
  enhance?: number;
}): Item {
  const enhance = args.enhance ?? 0;
  const mult = TIER_STAT_MULT[args.tier] * RARITY_STAT_MULT[args.rarity];
  const base = SLOT_BASE_STATS[args.slot];
  const stats: ItemStats = {
    hp: Math.round(base.hp * mult),
    attack: Math.round(base.attack * mult),
    armor: Math.round(base.armor * mult),
    armorBreak: Math.round(base.armorBreak * mult),
    heal: Math.round(base.heal * mult),
  };
  return {
    id: args.id,
    name: `${TIER_NAME[args.tier]} ${SLOT_NOUN[args.slot]}`,
    slot: args.slot,
    tier: args.tier,
    rarity: args.rarity,
    enhance,
    stats,
  };
}

export function rollDrop(args: {
  nextUnit: () => number;
  tier: Tier;
  pity: PityState;
  id: string;
}): { item: Item; pity: PityState } {
  const rolled = rollRarity(args.nextUnit(), args.tier, args.pity);
  const slotIndex = Math.floor(args.nextUnit() * GEAR_SLOTS.length) % GEAR_SLOTS.length;
  const slot = GEAR_SLOTS[slotIndex] ?? 'weapon';
  return {
    item: makeItem({ id: args.id, slot, tier: args.tier, rarity: rolled.rarity }),
    pity: rolled.pity,
  };
}

export function rollFarmDrops(args: {
  count: number;
  tier: Tier;
  pity: PityState;
  seed: number;
  startingId: number;
}): { items: Item[]; pity: PityState; nextId: number } {
  let state = normalizeSeed(args.seed);
  let pity = { ...args.pity };
  let nextId = args.startingId;
  const items: Item[] = [];
  const nextUnit = () => {
    const rolled = rngNext(state);
    state = rolled.state;
    return rolled.value;
  };
  const count = Math.max(0, Math.min(8000, Math.floor(args.count)));
  for (let i = 0; i < count; i += 1) {
    const drop = rollDrop({ nextUnit, tier: args.tier, pity, id: `item-${nextId}` });
    nextId += 1;
    pity = drop.pity;
    items.push(drop.item);
  }
  return { items, pity, nextId };
}
